import React, { useState, useEffect, useRef } from 'react';

interface TrackerHistoryEntry {
  date: string;
  totalCalories: number;
  consumedCalories: number;
  remainingCalories: number;
  isGoalAchieved: boolean;
}

interface TrackerDayDonutChartProps {
  history?: TrackerHistoryEntry | null;
}

type ChartState = 'empty' | 'goal-achieved' | 'exceeded' | 'progress';

const getChartState = (history: TrackerHistoryEntry | null | undefined): ChartState => {
  if (!history || history.totalCalories === 0) {
    return 'empty';
  }
  if (history.isGoalAchieved) {
    return 'goal-achieved';
  }
  if (history.remainingCalories < 0) {
    return 'exceeded';
  }
  if (history.consumedCalories === 0) {
    return 'empty';
  }
  return 'progress';
};

const SIZE = 28;
const RADIUS = 13;
const STROKE_WIDTH = 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const MAX_DASH_LENGTH = CIRCUMFERENCE;

const TrackerDayDonutChart: React.FC<TrackerDayDonutChartProps> = ({ history }) => {
  const state = getChartState(history);

  const percentage =
    state === 'progress' && history
      ? Math.min(
          MAX_DASH_LENGTH,
          (history.consumedCalories / history.totalCalories) * MAX_DASH_LENGTH
        )
      : state === 'goal-achieved' || state === 'exceeded'
      ? MAX_DASH_LENGTH
      : 0;

  // Keep donut visible while animating to 0, then hide after transition ends
  // (same logic as TrackerProgress)
  const [isDonutVisible, setIsDonutVisible] = useState(percentage > 0);
  const [isHidingAfterZero, setIsHidingAfterZero] = useState(false);
  const previousPercentageRef = useRef<number>(percentage);

  useEffect(() => {
    const previous = previousPercentageRef.current;

    if (percentage > 0 || state === 'goal-achieved' || state === 'exceeded') {
      setIsDonutVisible(true);
      setIsHidingAfterZero(false);
    } else {
      if (previous > 0) {
        setIsDonutVisible(true);
        setIsHidingAfterZero(true);
      } else {
        setIsDonutVisible(false);
        setIsHidingAfterZero(false);
      }
    }

    previousPercentageRef.current = percentage;
  }, [percentage, state]);

  const handleTransitionEnd = (event: React.TransitionEvent<SVGCircleElement>) => {
    if (event.propertyName !== 'stroke-dasharray') return;
    if (isHidingAfterZero && percentage === 0) {
      setIsDonutVisible(false);
      setIsHidingAfterZero(false);
    }
  };

  const getDonutClassName = () => {
    if (state === 'goal-achieved') return 'donut goal-achieved';
    if (state === 'exceeded') return 'donut exceeded';
    return 'donut';
  };

  const showEmptyCircle = state === 'empty' && !isDonutVisible;

  return (
    <div className={`timeline-day-donut ${showEmptyCircle ? 'timeline-day-donut--empty' : ''}`}>
      {showEmptyCircle ? (
        <div className="timeline-day-donut__empty-circle" />
      ) : (
        <>
          <div className="timeline-day-donut__background" />
          {isDonutVisible && (
            <svg
              className="timeline-day-donut__svg"
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
            >
              <g
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
                transform={`translate(${SIZE / 2}, ${SIZE / 2}) rotate(-90) translate(${
                  -SIZE / 2
                }, ${-SIZE / 2})`}
              >
                <circle
                  className={getDonutClassName()}
                  fill="none"
                  strokeDasharray={`${percentage}, 900`}
                  strokeWidth={STROKE_WIDTH}
                  strokeLinecap="round"
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  onTransitionEnd={handleTransitionEnd}
                  style={{ '--endvalue': percentage } as React.CSSProperties}
                />
              </g>
            </svg>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(TrackerDayDonutChart);
